import { jsPDF } from 'jspdf';
import { RESULTADO_LABELS, ROL_FIRMA_LABELS, type EtapaFirma, type ResultadoPregunta, type RolFirma } from './permisos-trabajo';
import { diaEc } from './fechas';

// PDF del permiso de trabajo (Fase 0, bloque 5, A4): datos, checklist con sus
// respuestas (catálogo y preguntas propias), firmas de la verificación, fotos y
// firma de cierre. Las imágenes llegan como data URL (evidenciaComoDataUrl).

export interface PermisoPDFData {
  empresa:       string;
  tipo:          string;
  estado:        string;
  descripcion:   string;
  ubicacion:     string;
  vigenteDesde:  string;
  vigenteHasta:  string;
  autorizadoPor: string;
  ejecutadoPor:  string;
  trabajador?:   string | null;
  vigia?:        string | null;
  activadoEn?:   string | null;
  cerradoEn?:    string | null;
  notasCierre?:  string | null;
  preguntas: { texto: string; referencia?: string | null; resultado: ResultadoPregunta | null; observacion?: string | null; propia: boolean }[];
  firmas:    { etapa: EtapaFirma; rol: RolFirma; nombre: string; cedula?: string | null; firmadoEn: string; imagen: string | null }[];
  fotos:     { tomadaEn: string; imagen: string | null }[];
}

export function fechaHoraEC(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });
}

export function nombreArchivoPermiso(tipo: string, fechaIso: string): string {
  const limpio = tipo.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return `Permiso_${limpio}_${diaEc(fechaIso)}.pdf`;
}

const MARGEN = 15;
const ANCHO = 210 - MARGEN * 2;
const ALTO_UTIL = 297 - 18;
// Alto de un título de sección (3 mm antes, la línea y 6 mm después).
export const ALTO_TITULO = 9;
// Una fila de firmas (recuadro, nombre, rol y fecha).
export const ALTO_FILA_FIRMAS = 42;

/**
 * Alto que hay que reservar para que un título no quede solo al final de la
 * página: el título más lo primero que va debajo (bloque 5c, §17 ítem 89).
 */
export function reservaTitulo(altoPrimerBloque: number): number {
  return ALTO_TITULO + Math.max(0, altoPrimerBloque);
}

/** Medidas de una foto en el PDF: media página de ancho, alto máximo 110 mm. */
export function medidasFoto(propiedades: { width: number; height: number } | null): { ancho: number; alto: number } {
  let ancho = ANCHO / 2 - 3;
  let alto = ancho * 0.75;
  if (propiedades && propiedades.width > 0 && propiedades.height > 0) {
    alto = Math.min(ancho * (propiedades.height / propiedades.width), 110);
    ancho = alto * (propiedades.width / propiedades.height);
  }
  return { ancho, alto };
}

export function generarPermisoPDF(d: PermisoPDFData): jsPDF {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = MARGEN;

  const espacio = (alto: number) => {
    if (y + alto > ALTO_UTIL) { doc.addPage(); y = MARGEN; }
  };
  // `siguiente`: alto de lo primero que va debajo; si no entra junto con el
  // título, los dos pasan a la página siguiente.
  const titulo = (texto: string, siguiente = 3) => {
    espacio(reservaTitulo(siguiente));
    y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(29, 111, 163);
    doc.text(texto, MARGEN, y);
    doc.setDrawColor(203, 213, 225); doc.line(MARGEN, y + 1.5, MARGEN + ANCHO, y + 1.5);
    y += 6;
    doc.setTextColor(15, 23, 42);
  };
  const parrafo = (texto: string, tam = 9, negrita = false, sangria = 0) => {
    doc.setFont('helvetica', negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    const lineas = doc.splitTextToSize(texto, ANCHO - sangria) as string[];
    lineas.forEach(l => { espacio(tam * 0.45); doc.text(l, MARGEN + sangria, y); y += tam * 0.45; });
  };
  const campo = (etiqueta: string, valor: string) => {
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    espacio(5);
    doc.text(`${etiqueta}:`, MARGEN, y);
    doc.setFont('helvetica', 'normal');
    const lineas = doc.splitTextToSize(valor || '—', ANCHO - 42) as string[];
    lineas.forEach((l, i) => { if (i > 0) espacio(4); doc.text(l, MARGEN + 42, y); y += 4.2; });
  };

  // Encabezado
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 26, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text('Permiso de trabajo', MARGEN, 12);
  doc.setFontSize(10); doc.setFont('helvetica', 'normal');
  doc.text(`${d.tipo} · ${d.empresa}`, MARGEN, 19);
  doc.text(`Estado: ${d.estado}`, 210 - MARGEN, 12, { align: 'right' });
  doc.setTextColor(15, 23, 42);
  y = 34;

  titulo('Datos del permiso');
  campo('Descripción', d.descripcion);
  campo('Ubicación', d.ubicacion);
  campo('Vigente desde', d.vigenteDesde);
  campo('Vigente hasta', d.vigenteHasta);
  campo('Autorizado por', d.autorizadoPor);
  campo('Ejecutado por', d.ejecutadoPor);
  if (d.trabajador) campo('Trabajador registrado', d.trabajador);
  if (d.vigia) campo('Vigía', d.vigia);
  if (d.activadoEn) campo('Activado', d.activadoEn);
  if (d.cerradoEn) campo('Cerrado', d.cerradoEn);

  titulo('Verificación (checklist)');
  d.preguntas.forEach((p, i) => {
    const res = p.resultado ? RESULTADO_LABELS[p.resultado] : 'Sin responder';
    espacio(9);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    if (p.resultado === 'no_cumple') doc.setTextColor(185, 28, 28);
    else if (p.resultado === 'cumple') doc.setTextColor(21, 128, 61);
    else doc.setTextColor(71, 85, 105);
    doc.text(res, MARGEN + ANCHO, y, { align: 'right' });
    doc.setTextColor(15, 23, 42);
    const prefijo = `${i + 1}. ${p.propia ? '(Pregunta propia) ' : ''}`;
    doc.setFont('helvetica', 'normal');
    const lineas = doc.splitTextToSize(prefijo + p.texto, ANCHO - 26) as string[];
    lineas.forEach(l => { espacio(4.2); doc.text(l, MARGEN, y); y += 4.2; });
    doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
    if (p.referencia) { espacio(3.6); doc.text(`Ref.: ${p.referencia}`, MARGEN + 4, y); y += 3.6; }
    if (p.observacion) {
      (doc.splitTextToSize(`Observación: ${p.observacion}`, ANCHO - 4) as string[])
        .forEach(l => { espacio(3.6); doc.text(l, MARGEN + 4, y); y += 3.6; });
    }
    doc.setTextColor(15, 23, 42);
    y += 1.5;
  });

  const bloqueFirmas = (lista: PermisoPDFData['firmas']) => {
    const anchoCol = (ANCHO - 6) / 2;
    lista.forEach((f, i) => {
      const col = i % 2;
      if (col === 0) espacio(42);
      const x = MARGEN + col * (anchoCol + 6);
      const y0 = y;
      doc.setDrawColor(203, 213, 225); doc.rect(x, y0, anchoCol, 24);
      if (f.imagen) {
        try { doc.addImage(f.imagen, 'PNG', x + 2, y0 + 1, anchoCol - 4, 22); } catch { /* imagen dañada: se deja el recuadro */ }
      }
      doc.setFontSize(8.5); doc.setFont('helvetica', 'bold');
      doc.text(f.nombre, x, y0 + 28);
      doc.setFont('helvetica', 'normal');
      doc.text(`${ROL_FIRMA_LABELS[f.rol]}${f.cedula ? ` · C.I. ${f.cedula}` : ''}`, x, y0 + 32);
      doc.text(fechaHoraEC(f.firmadoEn), x, y0 + 36);
      if (col === 1 || i === lista.length - 1) y = y0 + 41;
    });
  };

  const verificacion = d.firmas.filter(f => f.etapa === 'verificacion');
  titulo('Firmas de la verificación', verificacion.length > 0 ? ALTO_FILA_FIRMAS : 5);
  if (verificacion.length === 0) parrafo('Sin firmas registradas.');
  else bloqueFirmas(verificacion);

  const propiedades = (imagen: string) => {
    try { return doc.getImageProperties(imagen); } catch { return null; /* se usa 4:3 */ }
  };
  const primeraFoto = d.fotos.find(f => f.imagen);
  titulo('Fotos de la verificación', primeraFoto?.imagen ? medidasFoto(propiedades(primeraFoto.imagen)).alto + 8 : 5);
  if (d.fotos.length === 0) parrafo('Sin fotos registradas.');
  d.fotos.forEach((f, i) => {
    if (!f.imagen) return;
    const { ancho, alto } = medidasFoto(propiedades(f.imagen));
    espacio(alto + 8);
    try { doc.addImage(f.imagen, 'JPEG', MARGEN, y, ancho, alto); } catch { /* imagen dañada */ }
    y += alto + 4;
    doc.setFontSize(8); doc.setTextColor(100, 116, 139);
    doc.text(`Foto ${i + 1} · ${fechaHoraEC(f.tomadaEn)}`, MARGEN, y);
    doc.setTextColor(15, 23, 42);
    y += 5;
  });

  const cierre = d.firmas.filter(f => f.etapa === 'cierre');
  if (cierre.length > 0 || d.notasCierre) {
    titulo('Cierre del permiso', cierre.length > 0 && !d.notasCierre ? ALTO_FILA_FIRMAS + 2 : 5);
    if (d.notasCierre) parrafo(`Notas de cierre: ${d.notasCierre}`);
    y += 2;
    if (cierre.length > 0) bloqueFirmas(cierre);
  }

  const paginas = doc.getNumberOfPages();
  for (let p = 1; p <= paginas; p++) {
    doc.setPage(p);
    doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
    doc.text(`Generado con StorSafe · ${fechaHoraEC(new Date().toISOString())}`, MARGEN, 290);
    doc.text(`Página ${p} de ${paginas}`, 210 - MARGEN, 290, { align: 'right' });
  }
  return doc;
}
